import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PencilSimple, PlusCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Stage 4 — Portal "Add details" / "Edit details" widget.
 *
 * Shown on any portal ticket whose linked form has fillable_later fields.
 * Button label follows state:
 *   - "Add details" when awaiting_details is true (pending fields exist)
 *   - "Edit details" once everything's been filled
 *
 * Fillable-later fields stay editable indefinitely — never locked after
 * the first fill. The only lock is when the ticket reaches a terminal
 * status (resolved / withdrawn); in that case the widget renders a
 * read-only "locked" hint instead of the button.
 *
 * On save, we call `PATCH /portal/tickets/{id}/details` with the changed
 * answers only. The parent is expected to re-fetch the ticket to pick
 * up the recomputed subject, key_date, and awaiting_details flag.
 */
export function PortalDeferredDetails({ submission, ticketId, apiClient, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});

  if (!submission || !submission.editable_fields || submission.editable_fields.length === 0) {
    return null;
  }

  const awaiting = !!submission.awaiting_details;
  const locked = !!submission.locked;
  const pending = new Set(submission.pending_field_ids || []);
  const revisions = submission.revisions || [];

  const openDialog = () => {
    // Seed the form with current values so the user sees what's already there.
    const seed = {};
    for (const f of submission.editable_fields) {
      seed[f.id] = submission.current_values?.[f.id] ?? "";
    }
    setValues(seed);
    setOpen(true);
  };

  const save = async () => {
    // Only submit changed fields (compare to current_values).
    const changed = [];
    for (const f of submission.editable_fields) {
      const cur = submission.current_values?.[f.id] ?? "";
      const next = values[f.id] ?? "";
      if (String(cur ?? "") !== String(next ?? "")) {
        changed.push({ field_id: f.id, label: f.label, value: next });
      }
    }
    if (changed.length === 0) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const r = await apiClient.patch(`/portal/tickets/${ticketId}/details`, { answers: changed });
      const suffix = r.data?.awaiting_details ? "Some details still pending." : "All details supplied — thanks!";
      toast.success(`Saved ${changed.length} change${changed.length === 1 ? "" : "s"}. ${suffix}`);
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Locked (terminal ticket) — render a hint rather than a button.
  if (locked) {
    return (
      <div className="hairline rounded-lg bg-slate-50 p-4" data-testid="portal-details-locked">
        <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">Deferred details</div>
        <div className="text-sm text-slate-700 mt-1">
          This ticket is closed — the deferred fields can no longer be edited.
        </div>
      </div>
    );
  }

  const label = awaiting ? "Add details" : "Edit details";
  const Icon = awaiting ? PlusCircle : PencilSimple;
  const pendingCount = pending.size;

  return (
    <div className="hairline rounded-lg bg-white p-4" data-testid="portal-details-widget">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">Deferred details</div>
          {awaiting ? (
            <div className="text-sm text-slate-800 mt-1" data-testid="portal-details-pending-copy">
              {pendingCount === 1
                ? "1 detail still needs an answer — the team is waiting on you."
                : `${pendingCount} details still need answers — the team is waiting on you.`}
            </div>
          ) : (
            <div className="text-sm text-slate-800 mt-1">
              All details supplied. You can still edit any of them here — the team will be notified.
            </div>
          )}
          {revisions.length > 0 && (
            <div className="text-[11px] text-slate-500 font-mono mt-2">
              {revisions.length} previous change{revisions.length === 1 ? "" : "s"} recorded.
            </div>
          )}
        </div>
        <Button
          onClick={openDialog}
          size="sm"
          className={awaiting ? "bg-amber-600 hover:bg-amber-700 h-8" : "bg-[#4f4c90] hover:bg-[#5F5CA5] h-8"}
          data-testid="portal-details-open-btn"
        >
          <Icon size={14} weight="duotone" /> {label}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {submission.editable_fields.map((f) => {
              const isPending = pending.has(f.id);
              return (
                <div key={f.id} data-testid={`portal-detail-field-${f.id}`}>
                  <Label className="text-xs flex items-center gap-2">
                    <span>{f.label}</span>
                    {isPending && (
                      <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 font-mono uppercase">Pending</span>
                    )}
                  </Label>
                  {f.help_text && (
                    <div className="text-[11px] text-slate-500 mt-1">{f.help_text}</div>
                  )}
                  <FieldInput
                    field={f}
                    value={values[f.id] ?? ""}
                    onChange={(v) => setValues((cur) => ({ ...cur, [f.id]: v }))}
                  />
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#4f4c90] hover:bg-[#5F5CA5]"
              data-testid="portal-details-save-btn"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const cls = "mt-1 bg-white border-slate-200";
  if (field.field_type === "long_text") {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${cls} min-h-20`} />;
  }
  if (field.field_type === "date") {
    return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} />;
  }
  if (field.field_type === "email") {
    return <Input type="email" value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} placeholder={field.placeholder || ""} />;
  }
  if (field.field_type === "select") {
    // Mirror the shadcn Select style used elsewhere so the modal doesn't
    // look like a raw browser dropdown.
    return (
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
      >
        <option value="">Choose…</option>
        {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.field_type === "multi_select") {
    // Same chip-toggle rendering as PublicForm so the field looks
    // identical whether it's being filled at submit time or edited later.
    const cur = Array.isArray(value) ? value : [];
    return (
      <div className="mt-1 flex flex-wrap gap-1.5">
        {(field.options || []).map((o) => {
          const on = cur.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(on ? cur.filter((x) => x !== o) : [...cur, o])}
              className={`text-xs px-3 py-1.5 rounded-md border ${on ? "bg-[#4f4c90] text-white border-[#4f4c90]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    );
  }
  if (field.field_type === "checkbox") {
    // §form-builder — matches PublicForm: two radios, boolean value.
    return (
      <div className="mt-1 flex items-center gap-4 text-sm text-slate-800">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={`yn-portal-${field.id}`}
            checked={value === true}
            onChange={() => onChange(true)}
          />
          Yes
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={`yn-portal-${field.id}`}
            checked={value === false}
            onChange={() => onChange(false)}
          />
          No
        </label>
      </div>
    );
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} placeholder={field.placeholder || ""} />;
}
